import CancelOutlined from '@mui/icons-material/CancelOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MuiTextField, { type TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField';
import { forwardRef, useState } from 'react';

import { InfoIconButton } from '../InfoIconButton';

export type TextFieldProps = Omit<MuiTextFieldProps, 'label'> & {
  clearAriaLabel?: string;
  clearable?: boolean;
  infoTooltip?: string;
  loading?: boolean;
  label?: string;
  onClear?: () => void;
};

export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(
  (
    {
      clearAriaLabel = 'Clear',
      clearable = false,
      defaultValue,
      infoTooltip,
      loading,
      onChange,
      onClear,
      value,
      ...rest
    },
    ref
  ) => {
    const callerInputSlotProps = typeof rest.slotProps?.input === 'object' ? rest.slotProps.input : {};
    const isControlled = value != null;
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? '');
    const fieldValue = isControlled ? value : uncontrolledValue;
    const showClearButton = clearable && onClear != null && !rest.disabled && String(fieldValue) !== '';
    const callerEndAdornment = callerInputSlotProps.endAdornment;
    const handleChange: NonNullable<MuiTextFieldProps['onChange']> = (event) => {
      if (clearable && !isControlled) {
        setUncontrolledValue(event.target.value);
      }

      onChange?.(event);
    };
    const handleClear = () => {
      if (clearable && !isControlled) {
        setUncontrolledValue('');
      }

      onClear?.();
    };
    const clearAdornment = showClearButton ? (
      <InputAdornment position="end">
        <IconButton
          aria-label={clearAriaLabel}
          edge="end"
          onClick={handleClear}
          onMouseDown={(event) => event.preventDefault()}
          size="small"
        >
          <CancelOutlined fontSize="small" />
        </IconButton>
      </InputAdornment>
    ) : null;
    const loadingAdornment = loading ? (
      <InputAdornment position="end">
        <CircularProgress size={rest.size === 'small' ? 16 : 20} />
      </InputAdornment>
    ) : null;
    const inputSlotProps =
      loading || clearable
        ? {
            ...callerInputSlotProps,
            endAdornment:
              callerEndAdornment || clearAdornment || loadingAdornment ? (
                <>
                  {callerEndAdornment}
                  {clearAdornment}
                  {loadingAdornment}
                </>
              ) : undefined,
          }
        : rest.slotProps?.input;

    const callerHtmlInput = rest.slotProps?.htmlInput;
    const isCallerHtmlInputCallback = typeof callerHtmlInput === 'function';
    const callerHtmlInputProps =
      !isCallerHtmlInputCallback && callerHtmlInput != null ? (callerHtmlInput as Record<string, unknown>) : {};
    const shouldDefaultInputMode =
      rest.type === 'number' && !isCallerHtmlInputCallback && callerHtmlInputProps.inputMode == null;
    const htmlInputProps = shouldDefaultInputMode
      ? { inputMode: 'decimal' as const, ...callerHtmlInputProps }
      : callerHtmlInput;

    const slotProps = {
      ...rest.slotProps,
      htmlInput: htmlInputProps,
      input: inputSlotProps,
    };

    // Mirror the Select wrapper: render inside a flex Box whose width is auto when
    // not fullWidth. In a stretch layout (e.g. Form's flex column) the Box stretches
    // but the input stays content-width, so fullWidth={false} is respected instead of
    // the field being stretched to full width.
    return (
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          width: rest.fullWidth ? '100%' : undefined,
        }}
      >
        <MuiTextField
          {...rest}
          defaultValue={clearable ? undefined : defaultValue}
          onChange={handleChange}
          ref={ref}
          slotProps={slotProps}
          value={clearable ? fieldValue : value}
        />
        {infoTooltip ? <InfoIconButton tooltip={infoTooltip} sx={{ marginTop: 1 }} /> : null}
      </Box>
    );
  }
);

TextField.displayName = 'TextField';
